/**
 * Class that implements a Point.
 * 
 * @author Daniel Bart
 * 
 */
var Point = function(x, y) {
    this.x = (typeof x === 'undefined') ? 0 : x;
    this.y = (typeof y === 'undefined') ? 0 : y;
};

Point.prototype = {
    /**
     * Clones the point
     * @return {Point} Clone of the point
     */
    clone: function() {
        return new Point(this.x, this.y);
    },

    /**
     * Compares the point
     * @param  {Point} point     Point to compare
     * @param  {float} tolerance Tolerance value for comparaison
     * @return {Boolean}           True if points are equal
     */
    equals: function(point, tolerance) {
        var t = (typeof tolerance !== 'undefined') ? tolerance : 0;
        if (point) {
            if (t != 0) {
                return Math.abs(this.x - point.x) < t && Math.abs(this.y - point.y) < t;
            } else {
                return Math.abs(this.x - point.x) == 0 && Math.abs(this.y - point.y) == 0;
            }
        }
        return false;
    },

    /**
     * is Valid point
     * @return {Boolean} 
     */
    isValid: function() {
        return !(isNaN(this.x) || isNaN(this.y));
    },

    /**
     * Generates a string that represents the point.
     * @return {String} Point's coordinates in string format
     */
    toString: function() {
        return this.x + ", " + this.y;
    },

    /**
     * Transforms a point using a matrix
     * @param  {Raphael.Matrix} matrix Matrix (Rapahel JS)
     * @return {Point}        This, transformed
     */
    transform: function(matrix) {
        var newX = matrix.x(this.x, this.y)
        var newY = matrix.y(this.x, this.y)
        this.x = newX;
        this.y = newY;
        return this;
    },

    /**
     * Creates a new point with specified offset
     * @param  {Point} point offset values
     * @return {Point}       new point using offset
     */
    getOffset: function(point) {
        return new Point(point.x - this.x, point.y - this.y);
    },

    /**
     * Subtracts the specified point to the point
     * @param  {Point} point point to subtract
     * @return {Point}       This (modified)
     */
    subtract: function(point) {
        var result = this.getOffset(new Point(-point.x, -point.y));
        return result;
    },

    /**
     * Rotates the point
     * @param  {float} angle  Angle of rotation
     * @param  {Point} anchor anchor of rotation
     * @return {Point}        This (transformed)
     */
    rotate: function(angle, anchor) {
        angle = (typeof angle !== 'undefined') ? angle : 0;
        if (angle % 360 != 0) {
            var origin = (typeof anchor !== 'undefined' && anchor !== null) ? anchor : new Point();
            var wPoint = this.clone();
            var result = new Point();
            var theta = angle * (Math.PI / 180);
            result.x = Math.cos(theta) * (wPoint.x - origin.x) - Math.sin(theta) * (wPoint.y - origin.y) + origin.x;
            result.y = Math.sin(theta) * (wPoint.x - origin.x) + Math.cos(theta) * (wPoint.y - origin.y) + origin.y;
            this.x = result.x;
            this.y = result.y;
            return this;
        }
        return this;
    },

    /**
     * Retrieves the distance from this point to the specified point
     * @param  {Point} point reference point.  if not specified, 0, 0 is used.
     * @return {float}       distance
     */
    getDistance: function(point) {
        point = (!(point instanceof Point)) ? new Point(0, 0) : point;
        var dx = this.x - point.x;
        var dy = this.y - point.y;
        return Math.sqrt(dx * dx + dy * dy);
    },

    /**
     * Offset point by specified values
     * @param  {float} x X offset
     * @param  {float} y Y offset
     * @return {Point}   this
     */
    offset: function(x, y) {
        this.x = this.x + x;
        this.y = this.y + y;
        return this;

    },

    /**
     * Offset point by specified point
     * @param  {Point} offset offset point
     * @return {Point}   this
     */
    offsetBy: function(offset) {
        return this.offset(offset.x, offset.y);
    },

    angleTo: function(point) {
        var PRECISION = 10000;
        return Math.atan2((point.y * PRECISION) - (this.y * PRECISION), (point.x * PRECISION) - (this.x * PRECISION)) * (180 / Math.PI);
    }

};

export default Point;